import React from 'react';
import styled from 'styled-components';
import { pretendardFont } from '../../styles/typography';
import moveArrowIcon from '../../assets/icons/move_arrow.svg';
import { openExternalLink, type CampaignItem } from '../../utils/listingData';

const CampaignCard = styled.a`
  ${pretendardFont}
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: 420px;
  padding: 32px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: #d9d9d9;
  text-decoration: none;
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 280px;
    padding: 24px 20px;
    border-radius: 24px;
  }
`;

const CardMedia = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const CardBottomShade = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 48.4%,
    rgba(0, 0, 0, 0.6) 100%
  );
  opacity: 1;
  transition: opacity 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 0;
  }
`;

const CardLimeOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: rgba(211, 238, 52, 0.9);
  opacity: 0;
  transition: opacity 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 1;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
`;

const CardArrowCircle = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: #111111;
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;

  ${CampaignCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CardArrow = styled.img`
  width: 24px;
  height: 24px;
`;

const CardTitle = styled.div`
  align-self: stretch;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: -0.45px;
  transition: color 0.25s ease;

  ${CampaignCard}:hover & {
    color: #111111;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
  }
`;

interface CampaignGridCardProps {
  item: CampaignItem;
}

const CampaignGridCard: React.FC<CampaignGridCardProps> = ({ item }) => (
  <CampaignCard
    href={item.campaignUrl}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(event) => openExternalLink(event, item.campaignUrl)}
    aria-label={item.imageAlt}
  >
    <CardMedia
      src={item.imageUrl}
      alt={item.imageAlt}
      loading="lazy"
      decoding="async"
    />
    <CardBottomShade />
    <CardLimeOverlay />
    <CardContent>
      <CardArrowCircle aria-hidden>
        <CardArrow src={moveArrowIcon} alt="" />
      </CardArrowCircle>
      <CardTitle>{item.titleLines.join(' ')}</CardTitle>
    </CardContent>
  </CampaignCard>
);

export default CampaignGridCard;
